
import IconButton from 'components/atoms/button/IconButton';
import Form from 'components/atoms/form/Form';
import withModal from 'lib/helpers/hoc/withModal'

import React, { FC } from 'react'
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Icon from 'components/atoms/icon/Icon';
import { IconType } from 'lib/helpers/constants/iconTypes';
import Label from 'components/atoms/input/input-label/InputLabel';
import CreatableSelect from 'react-select/creatable';
import { customStyles } from 'lib/helpers/configs/customStyles';
import { usePartners } from 'lib/hooks/queries/Partners/usePartnersForFilter';
import RangeSlider from 'components/atoms/range-slider/RangeSlider';
import { useAddCommisionCompanyCommission } from 'lib/hooks/mutations/Finances/useAddCommisionCompanyCommission';
import { usePartnerSingleProduct } from 'lib/hooks/queries/Partners/usePartnerSingleProduct';
import Input from 'components/atoms/input/Input';
import RadioButton from 'components/atoms/radio-button/RadioButton';
import CurrencyInput from 'components/atoms/input/currency-input/CurrencyInput';



export interface IAddNewCommissionCompanyProps {
    onConfirm: () => void;
    id?: string;
    productName?: string;
}

const AddNewCommissionCompany: FC<IAddNewCommissionCompanyProps> = ({ onConfirm, id, productName }) => {

    const { t } = useTranslation();

    const { control,
        formState: { errors },
        handleSubmit,
        watch,
        register,
        clearErrors,
    } = useForm({
        defaultValues: {
            finance_company_id: id,
            partner_id: null,
            partner_product_id: null,
            company_commission: 0,
            company_commission_on_recomandation_cases: 0,
            company_storno_deposit: 0,
            multiply_expected_commission: '',
            expected_commission_measurement_type: 'multiply',
        }
    });


    const { data: partnersData, isLoading: isLoadingPartner } = usePartners();

    const { data: partnerProductsData, isLoading: isLoadingProduct } = usePartnerSingleProduct({ base_product: productName, partner_id: watch('partner_id') });


    const { mutateAsync, isLoading } = useAddCommisionCompanyCommission()

    const onSubmit = (data: any) => {
        mutateAsync(data).then(() => {
            onConfirm()
        })
    };


    return (
        <Form onSubmit={handleSubmit(onSubmit)}>
            <div className="mb-0 flex flex-col gap-5">

                <div className="relative">
                    <Label text={t('Company')} />
                    <Controller
                        name="partner_id"
                        control={control}
                        rules={{ required: `${t('Company is required')}` }}
                        render={({ field }) => (
                            <CreatableSelect<any, false>
                                {...field}
                                placeholder={t('Choose company')}
                                isDisabled={isLoadingPartner}
                                options={
                                    (
                                        partnersData?.partners as unknown as Array<{
                                            name: string;
                                            id: string;
                                        }>
                                    )?.map?.((user) => ({
                                        label: user?.name,
                                        value: user?.id,
                                    })) ?? []
                                }
                                value={
                                    field?.value
                                        ? {
                                            value: field?.value,
                                            label:
                                                (
                                                    partnersData?.partners?.find?.(
                                                        (user: any) => user.id === field.value
                                                    ) as { name: string } | undefined
                                                )?.name || field.value,
                                        }
                                        : null
                                }
                                className="cursor-pointer font-inter-regular react-select-container"
                                classNamePrefix="react-select"
                                onChange={(selectedOption) => {
                                    const selectedValue = selectedOption
                                        ? selectedOption.value
                                        : null;
                                    field.onChange(selectedValue);

                                }}
                                styles={customStyles}
                                isValidNewOption={() => false}
                                menuPosition={"fixed"}
                                menuPortalTarget={document.body}
                            />
                        )}
                    />
                    {errors.partner_id?.message && (
                        <>
                            <div className="font-inter-regular text-xs text-red-600 w-max absolute bottom-[-16px] 2xl:bottom-[-20px]  left-[2px]">
                                <>{errors.partner_id?.message}</>
                            </div>
                        </>
                    )}
                </div>

                <div className="relative">
                    <div className='flex gep-2 items-center'>
                        <Label text={t('Product')} />
                        <div className='font-inter-medium text-sm text-theme mb-[8px]'>({t('optional')})</div>
                    </div>
                    <Controller
                        name="partner_product_id"
                        control={control}
                        render={({ field }) => (
                            <CreatableSelect<any, false>
                                {...field}
                                placeholder={t('Choose Product')}
                                options={
                                    partnerProductsData?.map?.((user: { product: string }) => ({
                                        label: user?.product,
                                        value: user?.product,
                                    })) ?? []
                                }
                                isDisabled={watch('partner_id') === null || isLoadingProduct}
                                value={
                                    field?.value
                                        ? {
                                            value: field?.value,
                                            label: field?.value,
                                        }
                                        : null
                                }
                                className="cursor-pointer font-inter-regular react-select-container"
                                classNamePrefix="react-select"
                                onChange={(selectedOption) => {
                                    const selectedValue = selectedOption
                                        ? selectedOption.value
                                        : null;
                                    field.onChange(selectedValue);

                                }}
                                styles={customStyles}
                                isValidNewOption={() => false}
                                menuPosition={"fixed"}
                                menuPortalTarget={document.body}
                            />
                        )}
                    />
                </div>

                <Controller
                    name="company_commission"
                    control={control}
                    rules={{ required: `${t('Company is required')}` }}
                    render={({ field }) => (
                        <RangeSlider
                            value={field.value}
                            onChange={field.onChange}
                            rightLabel='Worker'
                            leftLabel='Company Profit'
                        />
                    )}
                />
                <div className='border border-border p-3 rounded-lg'>
                    <Label>{t('Commisions on Recomandation Cases')}</Label>
                    <Controller
                        name="company_commission_on_recomandation_cases"
                        control={control}
                        rules={{ required: `${t('Company is required')}` }}
                        render={({ field }) => (
                            <RangeSlider
                                value={field.value}
                                onChange={field.onChange}
                                rightLabel='Worker'
                                leftLabel='Company Profit'
                            />
                        )}
                    />  
                </div>
                <Controller
                    name="company_storno_deposit"
                    control={control}
                    render={({ field }) => (
                        <RangeSlider
                            value={field.value}
                            onChange={field.onChange}
                            rightLabel=' '
                            leftLabel='Storno Deposit'
                        />
                    )}
                />
                <div className='grid grid-cols-4 gap-3 items-end border border-border p-3 rounded-lg'>
                    <div className="col-span-4">
                        <Label>{t('Expected Commission Measurement Type')}</Label>
                    </div>
                    <div className='col-span-2'>
                        <Controller
                            name={`expected_commission_measurement_type`}
                            control={control}
                            render={({ field }) => (
                                <RadioButton
                                    label={t('Multiplier')}
                                    isChecked={field.value === 'multiply'}
                                    value={'multiply'}
                                    onChange={() => {
                                        field.onChange('multiply');
                                        clearErrors(`multiply_expected_commission`)
                                    }}
                                    labelClassName="h-[41px] 2xl:h-[46px] items-center flex bg-white"
                                    iconWrapperClassName="!top-[20px] 2xl:!top-[22px] !right-[6px]"
                                    className="right-[8px]"
                                />
                            )}
                        />
                    </div>
                    <div className='col-span-2 '>
                        <Controller
                            name={`expected_commission_measurement_type`}
                            control={control}
                            render={({ field }) => (
                                <RadioButton
                                    label={t('Fixed Amount')}
                                    isChecked={field.value === 'fixed_amount'}
                                    value={'fixed_amount'}
                                    onChange={() => {
                                        field.onChange('fixed_amount');
                                        clearErrors(`multiply_expected_commission`)
                                    }}
                                    labelClassName="h-[41px] 2xl:h-[46px] items-center flex bg-white"
                                    iconWrapperClassName="!top-[20px] 2xl:!top-[22px] !right-[6px]"
                                    className="right-[8px]"
                                />
                            )}
                        />
                    </div>
                    <div className='col-span-4'>
                        {watch(`expected_commission_measurement_type`) === 'multiply' ? (
                            <Input
                                {...register(`multiply_expected_commission`)}
                                placeholder={'2x'}
                                type="number"
                                step="any"
                                error={errors?.multiply_expected_commission}
                            />
                        ) : (
                            <Controller
                                name="multiply_expected_commission"
                                control={control}
                                render={({ field }) => (
                                    <CurrencyInput
                                        placeholder={t("100 CHF")}
                                        prefix="CHF "
                                        value={field.value || ""}
                                        onValueChange={(value: any) => {
                                            field.onChange(value || "");

                                        }}

                                        error={errors?.multiply_expected_commission}
                                    />
                                )}
                            />
                        )}
                    </div>
                </div>

            </div>
            <div className="flex flex-row justify-end gap-5 pt-10">
                <IconButton className="" type='submit' icon={<Icon iconType={IconType.PLUS_ICON} />} disabled={isLoading}>
                    {t('Add commission')}
                </IconButton>
            </div>
        </Form >
    )
}

export default withModal(AddNewCommissionCompany)